import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TrendingUp } from "lucide-react";
import { ScreenHeader } from "@/components/ScreenHeader";
import { api } from "@/api/client";
import { colors, spacing, fonts } from "@/theme";

const RANGES = [
  { key: 7, label: "7 days" },
  { key: 14, label: "14 days" },
  { key: 30, label: "30 days" },
];

const DOW = ["S", "M", "T", "W", "T", "F", "S"];

function dayLabel(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return { dow: DOW[dt.getDay()], num: String(d).padStart(2, "0") };
}

function cellBackground(count, peak) {
  if (count === 0) return colors.surfaceTertiary;
  const t = Math.min(1, Math.log(count + 1) / Math.log(peak + 1));
  const from = { r: 0xDB, g: 0xEA, b: 0xFE };
  const to = { r: 0x1D, g: 0x4E, b: 0xD8 };
  const r = Math.round(from.r + (to.r - from.r) * t);
  const g = Math.round(from.g + (to.g - from.g) * t);
  const b = Math.round(from.b + (to.b - from.b) * t);
  return `rgb(${r},${g},${b})`;
}

function cellText(count, peak) {
  if (count === 0) return colors.onSurfaceTertiary;
  const t = Math.min(1, Math.log(count + 1) / Math.log(peak + 1));
  return t > 0.5 ? colors.onBrandPrimary : colors.onSurface;
}

export default function Analytics() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const d = await api(`/reports/heatmap?days=${days}`);
      setData(d);
    } catch (e) {
      setData(null);
    }
  }, [days]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  const dayHeaders = useMemo(() => (data ? data.days.map(dayLabel) : []), [data]);
  const peak = data?.peak ?? 0;
  const activeDrivers = data ? data.drivers.filter((d) => d.total > 0).length : 0;
  const busiestDay = useMemo(() => {
    if (!data) return null;
    const totalsPerDay = data.days.map((_, i) => data.drivers.reduce((sum, d) => sum + d.counts[i], 0));
    const idx = totalsPerDay.reduce((best, cur, i) => (cur > totalsPerDay[best] ? i : best), 0);
    return totalsPerDay[idx] > 0 ? { label: dayHeaders[idx], count: totalsPerDay[idx] } : null;
  }, [data, dayHeaders]);

  return (
    <div style={{ flex: 1, backgroundColor: colors.surface }}>
      <ScreenHeader title="Analytics" subtitle={data ? `${data.total} moves · last ${days} days` : "Loading…"} />
      <div style={s.body}>
        <div style={s.rangeRow}>
          {RANGES.map((r) => {
            const active = days === r.key;
            return (
              <button key={r.key} data-testid={`analytics-range-${r.key}`} onClick={() => setDays(r.key)}
                style={{ ...s.rangeChip, ...(active ? s.rangeChipActive : {}) }}>
                <span style={{ ...s.rangeText, color: active ? colors.onBrandPrimary : colors.onSurfaceTertiary }}>{r.label}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div style={s.spinnerWrap}><div className="yo-spinner" /></div>
        ) : !data ? (
          <div style={s.empty}>Could not load analytics.</div>
        ) : (
          <>
            <div style={s.kpiRow}>
              <div style={s.kpi}><div style={s.kpiVal}>{data.total}</div><div style={s.kpiLabel}>Total moves</div></div>
              <div style={s.kpi}><div style={s.kpiVal}>{activeDrivers}</div><div style={s.kpiLabel}>Active drivers</div></div>
              <div style={s.kpi}><div style={s.kpiVal}>{peak}</div><div style={s.kpiLabel}>Daily peak</div></div>
            </div>

            {busiestDay && (
              <div style={s.callout}>
                <TrendingUp size={18} color={colors.brandPrimary} />
                <span style={s.calloutText}>
                  Busiest day: {busiestDay.label.dow} {busiestDay.label.num} · {busiestDay.count} {busiestDay.count === 1 ? "move" : "moves"}
                </span>
              </div>
            )}

            <HeatmapSection title="Moves per driver" subtitle="Rows sorted by total" days={dayHeaders} rows={data.drivers} peak={peak} emptyLabel="No acknowledged moves in this window." testIDPrefix="driver" />
            <HeatmapSection title="Moves per yard" subtitle="Compare load across yards" days={dayHeaders} rows={data.yards} peak={peak} emptyLabel="No moves recorded for any yard." testIDPrefix="yard" />

            <div style={s.legendWrap}>
              <span style={s.legendLabel}>Less</span>
              {[0, 1, 2, 3, 5, peak || 1].map((n, i) => (
                <div key={`${n}-${i}`} style={{ ...s.legendCell, backgroundColor: cellBackground(n, peak || 1) }} />
              ))}
              <span style={s.legendLabel}>More</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function HeatmapSection({ title, subtitle, days, rows, peak, emptyLabel, testIDPrefix }) {
  return (
    <div style={s.section}>
      <div style={s.sectionHead}>
        <div style={s.sectionTitle}>{title}</div>
        <div style={s.sectionSub}>{subtitle}</div>
      </div>
      {rows.length === 0 ? (
        <div style={s.empty}>{emptyLabel}</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <div style={{ display: "inline-block", minWidth: "min-content" }}>
            <div style={s.gridRow}>
              <div style={{ ...s.labelCell, backgroundColor: "transparent", borderWidth: 0 }} />
              {days.map((d, i) => (
                <div key={`h-${i}`} style={s.headerCell}>
                  <div style={s.headerDow}>{d.dow}</div>
                  <div style={s.headerNum}>{d.num}</div>
                </div>
              ))}
              <div style={{ ...s.totalCell, backgroundColor: "transparent", borderWidth: 0 }}>
                <span style={s.totalHead}>Σ</span>
              </div>
            </div>
            {rows.map((row) => (
              <div key={row.name} style={s.gridRow} data-testid={`${testIDPrefix}-row-${row.name}`}>
                <div style={s.labelCell}><span style={s.labelText}>{row.name}</span></div>
                {row.counts.map((c, i) => (
                  <div key={`${row.name}-${i}`} style={{ ...s.cell, backgroundColor: cellBackground(c, peak) }} data-testid={`${testIDPrefix}-cell-${row.name}-${i}`}>
                    <span style={{ ...s.cellText, color: cellText(c, peak) }}>{c || ""}</span>
                  </div>
                ))}
                <div style={s.totalCell}><span style={s.totalText}>{row.total}</span></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const CELL = 36;

const s = {
  body: { padding: spacing.xl, display: "flex", flexDirection: "column", gap: spacing.lg, maxWidth: 900 },
  rangeRow: { display: "flex", gap: spacing.sm },
  rangeChip: { padding: `8px ${spacing.md}px`, borderWidth: 2, borderStyle: "solid", borderColor: colors.borderStrong, backgroundColor: colors.surfaceSecondary, cursor: "pointer" },
  rangeChipActive: { backgroundColor: colors.brandPrimary, borderColor: colors.brandPrimary },
  rangeText: { ...fonts.monoBold, fontSize: 11, letterSpacing: 0 },
  spinnerWrap: { display: "flex", justifyContent: "center", marginTop: 40 },
  kpiRow: { display: "flex", gap: spacing.sm },
  kpi: { flex: 1, borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, padding: spacing.md, textAlign: "center" },
  kpiVal: { ...fonts.display, fontSize: 26, color: colors.onSurface },
  kpiLabel: { ...fonts.mono, fontSize: 10, color: colors.onSurfaceTertiary, marginTop: 2 },
  callout: { display: "flex", alignItems: "center", gap: spacing.sm, borderWidth: 2, borderStyle: "solid", borderColor: colors.brandPrimary, backgroundColor: colors.brandTertiary, padding: `${spacing.sm}px ${spacing.md}px` },
  calloutText: { ...fonts.monoBold, fontSize: 12, color: colors.onBrandTertiary, letterSpacing: 0 },
  section: { borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, padding: spacing.md },
  sectionHead: { marginBottom: spacing.sm },
  sectionTitle: { ...fonts.display, fontSize: 16, color: colors.onSurface },
  sectionSub: { ...fonts.mono, fontSize: 11, color: colors.onSurfaceTertiary, marginTop: 2 },
  gridRow: { display: "flex", alignItems: "center", marginBottom: 2 },
  labelCell: { width: 120, height: CELL, display: "flex", alignItems: "center", padding: `0 ${spacing.sm}px`, borderWidth: 1, borderStyle: "solid", borderColor: colors.border, backgroundColor: colors.surface, overflow: "hidden" },
  labelText: { ...fonts.monoBold, fontSize: 11, color: colors.onSurface, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" },
  headerCell: { width: CELL, height: CELL, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginLeft: 2, backgroundColor: colors.surface, borderWidth: 1, borderStyle: "solid", borderColor: colors.border },
  headerDow: { ...fonts.monoBold, fontSize: 9, color: colors.onSurfaceTertiary },
  headerNum: { ...fonts.monoBold, fontSize: 11, color: colors.onSurface },
  cell: { width: CELL, height: CELL, display: "flex", alignItems: "center", justifyContent: "center", marginLeft: 2, borderWidth: 1, borderStyle: "solid", borderColor: colors.border },
  cellText: { ...fonts.monoBold, fontSize: 12 },
  totalCell: { width: CELL + 8, height: CELL, display: "flex", alignItems: "center", justifyContent: "center", marginLeft: 4, backgroundColor: colors.onSurface, borderWidth: 1, borderStyle: "solid", borderColor: colors.onSurface },
  totalHead: { ...fonts.monoBold, fontSize: 12, color: colors.onSurface },
  totalText: { ...fonts.monoBold, fontSize: 12, color: colors.onSurfaceInverse },
  legendWrap: { display: "flex", alignItems: "center", gap: 4, justifyContent: "center", paddingTop: spacing.sm },
  legendLabel: { ...fonts.mono, fontSize: 10, color: colors.onSurfaceTertiary },
  legendCell: { width: 18, height: 18, borderWidth: 1, borderStyle: "solid", borderColor: colors.border },
  empty: { textAlign: "center", padding: spacing.lg, ...fonts.monoBold, color: colors.onSurfaceTertiary },
};
